import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { useNuxtApp } from '#app'
import { Service } from '@/_api/models/service'
import { db, toStorableData, type StoredRow } from '@/lib/db'
import { useAuthStore } from '@/stores/useAuthStore'

interface SyncState {
  running: boolean
  progress: number | null
  date: Date | null
  error: boolean
}

const SYNC_TTL_MS = 5 * 60 * 1000

const listOrder = (item: { listOrder?: number } | null | undefined): number => {
  if (item?.listOrder == null) return Number.MAX_SAFE_INTEGER

  const value = Number(item.listOrder)
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER
}

const compareListOrder = (
  left: { listOrder?: number } | null | undefined,
  right: { listOrder?: number } | null | undefined,
): number => listOrder(left) - listOrder(right)

/**
 * Hand-rolled store (distinct from `useSyncData`) because `$api.booking.services`
 * returns a bookable-subset view of services, not the paginated delta sync
 * that the other entity stores use. Storage layer moved to Dexie alongside
 * the rest — see `app/lib/db.ts` and .docs/performance.md.
 */
export const useServiceStore = defineStore('service', () => {
  const { $api, $grants } = useNuxtApp()
  const authStore = useAuthStore()

  const accountId = computed(() => authStore.activeGuid)

  const services = ref<Service[]>([])
  const hydrated = ref(false)

  // Secondary index: Map<objectID, Service> for O(1) findById / getServiceByID.
  // Matches the pattern used by useSyncData. See .docs/performance.md.
  const byId = shallowRef(new Map<string, Service>())

  const rebuildIndex = (list: Service[]) => {
    const next = new Map<string, Service>()
    for (const item of list) {
      const id = item.objectID()
      if (id) next.set(id, item)
    }
    byId.value = next
  }

  const syncState = ref<SyncState>({
    running: false,
    progress: null,
    date: null,
    error: false,
  })

  const isSyncing = computed(() => syncState.value.running)
  const syncProgress = computed(() => syncState.value.progress)
  const syncDate = computed(() => syncState.value.date)
  const count = computed(() => services.value.length)
  const all = computed(() => [...services.value].sort(compareListOrder))

  const syncStateKey = (id: string) => `${id}.services`

  let inFlight: { accountId: string; promise: Promise<void>; controller: AbortController; refresh?: Promise<void> } | null = null

  watch(accountId, () => {
    inFlight?.controller.abort()
    services.value = []
    rebuildIndex([])
    hydrated.value = false
    syncState.value = { running: false, progress: null, date: null, error: false }
  }, { flush: 'sync' })

  const performSync = async (
    activeAccountId: string,
    signal: AbortSignal,
    { force = false, change = false }: { force?: boolean; change?: boolean } = {},
  ) => {
    let currentServices = services.value
    let lastSyncDate = syncState.value.date

    if (!hydrated.value || change) {
      let storedServices: Service[] = []
      let storedSync
      try {
        const storedRows = await db.services.where('accountId').equals(activeAccountId).toArray()
        storedSync = await db.syncStates.get(syncStateKey(activeAccountId))
        storedServices = storedRows.map(row => new Service(row.data))
      } catch (error) {
        storedSync = undefined
        console.error('[useServiceStore] Failed to read cache', error)
      }

      if (signal.aborted || accountId.value !== activeAccountId) return

      currentServices = storedServices
      services.value = currentServices
      rebuildIndex(services.value)
      lastSyncDate = storedSync?.date ?? null
      syncState.value = {
        running: false, progress: null, date: lastSyncDate, error: storedSync?.error ?? false,
      }

      hydrated.value = true
    }

    if (!force && lastSyncDate) {
      const age = Date.now() - new Date(lastSyncDate).getTime()
      if (age >= 0 && age < SYNC_TTL_MS) {
        return
      }
    }

    const date = new Date()

    syncState.value.running = true

    const sinceDate = force ? undefined : (lastSyncDate ?? undefined)

    let result: { items: Service[] } | undefined
    const errors: Error[] = []

    try {
      result = await $api.booking.services({
        modifiedSince: sinceDate,
        signal,
        accountId: activeAccountId,
      })
    } catch (e: any) {
      if (e?.status === 304) {
        // Data is up to date
      } else {
        errors.push(e)
      }
    }

    if (signal.aborted || accountId.value !== activeAccountId) return

    const nextServices = result?.items ?? currentServices
    const nextError = errors.length > 0
    const nextDate = nextError ? null : date

    if (accountId.value === activeAccountId) {
      services.value = nextServices
      rebuildIndex(services.value)
      syncState.value = { running: false, progress: null, date: nextDate, error: nextError }
    }

    // Persist. Unlike useSyncData, the booking-services endpoint isn't a
    // paginated delta (no `deleted` field) — it returns the current bookable
    // set as a whole. So we clear-then-bulkPut inside one transaction, which
    // keeps reads consistent throughout.
    try {
      const writeRows: StoredRow[] = nextServices.map((item) => ({
        accountId: activeAccountId,
        id: item.objectID() ?? '',
        data: toStorableData(item),
      })).filter(row => row.id)

      await db.transaction('rw', db.services, db.syncStates, async () => {
        await db.services.where('accountId').equals(activeAccountId).delete()
        if (writeRows.length) {
          await db.services.bulkPut(writeRows)
        }
        await db.syncStates.put({
          key: syncStateKey(activeAccountId),
          accountId: activeAccountId,
          entity: 'services',
          date: nextDate,
          error: nextError,
        })
      })
    } catch (writeError) {
      console.error('[useServiceStore] Failed to persist to Dexie', writeError)
    }
  }

  const sync = (options: { force?: boolean; change?: boolean } = {}): Promise<void> => {
    if ($grants && !$grants.all({ service: ['view'] })) {
      return Promise.resolve()
    }

    const activeAccountId = accountId.value
    if (!activeAccountId) return Promise.resolve()

    if (inFlight) {
      if (inFlight.accountId === activeAccountId && !inFlight.controller.signal.aborted) {
        if (options.force) {
          inFlight.refresh ??= inFlight.promise.then(() => {
            if (accountId.value === activeAccountId) return sync(options)
          })
          return inFlight.refresh
        }
        return inFlight.promise
      }

      return inFlight.promise.then(() => sync(options))
    }

    const controller = new AbortController()
    const pending = performSync(activeAccountId, controller.signal, options)
    const tracked = pending.finally(() => {
      if (inFlight?.promise === tracked) {
        inFlight = null
      }
    })

    inFlight = { accountId: activeAccountId, promise: tracked, controller }
    return tracked
  }

  const categorised = computed(() => {
    const categories: Record<number, { services: Service[] } & Record<string, any>> = {}

    for (const service of all.value) {
      const category = (service as any).category
      const idServiceCategory = category?.idServiceCategory || 0

      if (!categories[idServiceCategory]) {
        categories[idServiceCategory] = {
          ...category,
          services: [],
        }
      }

      categories[idServiceCategory].services.push(service)
    }

    return Object.values(categories)
      .map(category => ({
        ...category,
        services: [...category.services].sort(compareListOrder),
      }))
      .sort(compareListOrder)
  })

  const categories = computed(() => {
    const categoryMap: Record<number, any> = {}

    for (const service of all.value) {
      const category = (service as any).category
      const idServiceCategory = category?.idServiceCategory || 0

      if (!categoryMap[idServiceCategory]) {
        categoryMap[idServiceCategory] = category
      }
    }

    return Object.values(categoryMap).sort(compareListOrder)
  })

  // Both lookups route through the Map index. getServiceByID preserved for
  // callsite compat with the old typed-id signature. See .docs/performance.md.
  const findById = (id: string | number) => byId.value.get(id.toString())
  const getServiceByID = (idService: number | string) => findById(idService)

  return {
    services,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    syncDate,
    sync,
    findById,
    all,
    categorised,
    categories,
    getServiceByID,
  }
})
